import { Component, HostListener, Inject, OnInit, PLATFORM_ID, afterNextRender, computed, inject, signal } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { NotificationsComponent } from './shared/notification/notification.component';
import { HeaderComponent } from './shared/header/header.component';
import { CommonModule, PlatformLocation, isPlatformBrowser } from '@angular/common';
import { MobileFooterMenuComponent } from './shared/mobile-footer-menu/mobile-footer-menu.component';
import { SignalsStoreService } from './shared/signals-store.service';
import { CartPreviewComponent } from './cart-preview/cart-preview.component';
import { AppService } from './app.service';
import { ResolutionService } from './shared/resolution.service';
import { ProductAddedNotificationComponent } from './shared/product-added-notification/product-added-notification.component';
import { toObservable } from '@angular/core/rxjs-interop';
import { combineLatest, tap } from 'rxjs';
import { environment } from '../environments/environment';
import { LocalStorageService } from './shared/local-storage.service';
import { LOCALSTORAGE_KEYS } from './shared/constants/databases';
import { OrderService } from './shared/order.service';
import { KlaviyoService } from './shared/klaviyo/klaviyo.service';
import { ScrollToTopDirective } from './shared/directives/scroll-to-top.directive';
import { GlobalMessageComponent } from './shared/global-message/global-message.component';
import { LoadingService } from './shared/loading.service';
import { LoadingComponent } from './shared/loading/loading.component';
import { PromotionsService } from './shared/promotions.service';
import { MarketingReferral } from './shared/types/common.types';
import { checkSignupCouponCodes } from './shared/common/singup.utils';
import { CanonicalService } from './shared/canonical.service';
import { AttributionService } from './shared/attribution/attribution.service';
import { DataLayerService } from './shared/data-layer/data-layer.service';

@Component({
  selector: 'app-root',
  imports: [
    RouterOutlet,
    MatProgressBarModule,
    NgbModule,
    NotificationsComponent,
    HeaderComponent,
    CommonModule,
    MobileFooterMenuComponent,
    CartPreviewComponent,
    ProductAddedNotificationComponent,
    ScrollToTopDirective,
    GlobalMessageComponent,
    LoadingComponent
  ],
  providers: [AppService],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss'
})
export class AppComponent implements OnInit {
  title = 'ecommerce';

  private signalStoreService = inject(SignalsStoreService);
  private router = inject(Router);
  private activatedRoute = inject(ActivatedRoute);
  private platformLocation = inject(PlatformLocation);
  private appService = inject(AppService);
  private resolutionService = inject(ResolutionService);
  private localStorageService = inject(LocalStorageService);
  private orderService = inject(OrderService);
  private klaviyoService = inject(KlaviyoService);
  loadingService = inject(LoadingService);

  #promotionsService = inject(PromotionsService);
  #canonicalService = inject(CanonicalService);
  #attributionService = inject(AttributionService);
  #dataLayerService = inject(DataLayerService);

  private containerClassesToApply = {
    layoutSignup: 'layout-signup',
    layoutStatic: 'layout-static',
    layoutShop: 'layout-shop',
    layoutSettings: 'layout-settings',
    layoutLogin: 'layout-login',
  };

  private containerClassesXRout: any = {
    '/home': this.containerClassesToApply.layoutStatic,
    '/pages': this.containerClassesToApply.layoutStatic,
    '/login': this.containerClassesToApply.layoutLogin,
    '/account-recovery': this.containerClassesToApply.layoutLogin,
    '/terms-and-conditions': this.containerClassesToApply.layoutLogin,
    '/settings': this.containerClassesToApply.layoutSettings,
    '/signup': this.containerClassesToApply.layoutSignup,
    '/shop': this.containerClassesToApply.layoutShop,
    '/favorites': this.containerClassesToApply.layoutShop,
    '/buy-again': this.containerClassesToApply.layoutShop,
    '/order': this.containerClassesToApply.layoutShop,
    '/upcoming-deliveries/custom-box': this.containerClassesToApply.layoutShop,
  }

  showAppHeader = signal(false);
  containerClassToAplly = signal('');

  hasSession = this.signalStoreService.hasSession;
  #hasSession$ = toObservable(this.hasSession);
  isSidebarOpen = this.signalStoreService.isSidebarOpen;
  hasGlobalMessages = computed(() => !!this.signalStoreService.globalMessages().length);
  showCartPreview = this.signalStoreService.showCartPreview;

  showAppCartPreview = computed(() => this.setUpCartPreviewDisplay());
  showJustAddedProduct = computed(() => this.setUpJustAddedProductDisplay());

  shouldShowCategoriesBar = computed(() => this.signalStoreService.shouldShowCategoriesBar());

  isMobile = computed(() => this.resolutionService.isMobile());
  routeSignal = signal(this.router.url);
  permissionsSignal = computed(() => this.signalStoreService.permissions());

  showMobileFooter = this.signalStoreService.isShowingMobileFooter;

  private isMobiles$ = toObservable(this.isMobile);
  private routeSignal$ = toObservable(this.routeSignal);
  private permissionsSignal$ = toObservable(this.permissionsSignal);

  constructor(@Inject(PLATFORM_ID) private platformId: any) {
    // Earliest browser-side hook we have: the first navigation has not resolved
    // yet, so window.location still carries the campaign params of the landing
    // URL. The service no-ops on the server and after the first capture.
    this.#attributionService.capture();

    this.#hydrateRouteDrivenState();
    this.#canonicalService.init();
    this.#identifyKlaviyoUser();
    afterNextRender(() => {
      this.appService.getMaintenanceState();
    })
  }

  #hydrateRouteDrivenState() {
    // With `enabledNonBlocking` initial navigation (Angular 19 default),
    // `router.url` is "/" during AppComponent construction. Read the real URL
    // from PlatformLocation, which is request-derived in SSR and
    // `location.pathname` in CSR, so the first paint already reflects the
    // correct header / layout class and avoids the CLS on /shop.
    const url = (this.platformLocation.pathname || '/').split('?')[0];
    this.routeSignal.set(url);
    this.showAppHeader.set(this.validateRoutesForHeader());
    this.showCartPreview.set(this.valiteRoutesForCartPreview());
    this.containerClassToAplly.set(this.setUpContainerClass());
  }

  @HostListener('document:click', ['$event'])
  handleClick(event: any) {
    if (!this.showJustAddedProduct()) return;
    const target = event.target as HTMLElement;
    if (target.closest('.just-added-product-notification')) {
      this.signalStoreService.justAddedProduct.set(null);
    }
  }

  ngOnInit(): void {
    // this.appService.getMaintenanceState();
    this.setUpRouteSubscription();

    if (!isPlatformBrowser(this.platformId)) return;
    this.activatedRoute.queryParams.subscribe(params => this.checkMarketingReferral(params));
    this.appService.addClassToBody(environment.config.bodyClass);

    combineLatest([this.isMobiles$, this.routeSignal$, this.permissionsSignal$]).pipe(
      tap(() => this.validateCategoriesBarDisplay())
    ).subscribe()

    this.router.events.subscribe(event => {
      if (event instanceof NavigationEnd)
        if (this.signalStoreService.hasSession())
          this.#dataLayerService.trackLoginStatusEvent();
    })

  }

  private setUpRouteSubscription() {
    this.router.events.subscribe(() => {
      // Refresh routeSignal FIRST — helpers below now read it instead of router.url
      this.routeSignal.set(this.router.url);
      this.showAppHeader.set(this.validateRoutesForHeader());
      this.showCartPreview.set(this.valiteRoutesForCartPreview());
      this.containerClassToAplly.set(this.setUpContainerClass());
    });
  }

  private setUpContainerClass(): string {
    const currentUrl = this.routeSignal();
    for (let key in this.containerClassesXRout) {
      if (currentUrl.includes(key)) {
        return this.containerClassesXRout[key];
      }
    }

    return this.containerClassesToApply.layoutShop; // This class by default;
  }


  validateRoutesForHeader(): boolean {
    const allowedRoutes = [
      '/',
      '/order',
      '/pages',
      '/product',
      '/settings',
      '/shop',
      '/favorites',
      '/buy-again',
      '/cart',
      '/upcoming-deliveries/custom-box'
    ];
    return this.validateRoutesToShowComponent(allowedRoutes);
  }

  valiteRoutesForCartPreview(): boolean {
    const allowedRoutes = [
      '/product',
      '/shop',
      '/favorites',
      '/buy-again'
    ];
    return this.validateRoutesToShowComponent(allowedRoutes, 'custom-box');
  }

  validateRoutesToShowComponent(allowedRoutes: string[], notAllowedRoute: string = 'null'): boolean {
    const currentUrl = this.routeSignal();
    return allowedRoutes.some(route => currentUrl.includes(route) && !currentUrl.includes(notAllowedRoute));
  }

  private setUpCartPreviewDisplay() {
    const hasSession = this.signalStoreService.hasSession();
    const order = this.signalStoreService.firebaseOrder();
    const odooOrder = this.orderService.odooOrder();
    const hasProducts = order?.products?.common?.length || order?.products?.subscription?.length;
    const hasOdooProducts = odooOrder?.products?.common?.length || odooOrder?.products?.subscription?.length;
    const shouldShowCartPreview = this.showCartPreview();

    return hasSession && (hasProducts || hasOdooProducts) && shouldShowCartPreview && !this.isMobile();
  }

  private setUpJustAddedProductDisplay() {
    return this.signalStoreService.hasSession() &&
      this.signalStoreService.justAddedProduct() &&
      this.isMobile();
  }

  private validateCategoriesBarDisplay() {
    let signupCustomBox = this.localStorageService.get(LOCALSTORAGE_KEYS.SIGNUP_CUSTOM_BOX);
    if (signupCustomBox && this.routeSignal().length > 1 && !(this.routeSignal().includes('/shop/custom-box/subscription'))) {
      this.router.navigate(['/signup']);
    }
    this.signalStoreService.isCustomBoxSignupFlow.set(!!signupCustomBox);
    const isMobile = this.isMobile();

    const shouldHideHeaderCategoriesNav = !!!(this.signalStoreService.permissions()?.shop.allowed ?? true);
    if (shouldHideHeaderCategoriesNav) {
      this.signalStoreService.shouldShowCategoriesBar.set(false);
    } else {
      const shouldShowCategoriesBar = !signupCustomBox && (!isMobile || this.validateRoutesToShowComponent(['/shop', '/favorites', '/buy-again', '/home']));
      this.signalStoreService.shouldShowCategoriesBar.set(shouldShowCategoriesBar);
    }

    const currentUrl = this.routeSignal();
    const isHomeRoute = currentUrl === '/' || currentUrl === '/home' || currentUrl.startsWith('/?');
    const shouldShowMobileFooter = isMobile && !signupCustomBox && !shouldHideHeaderCategoriesNav &&
      (isHomeRoute || this.validateRoutesToShowComponent(['/shop', '/favorites', '/buy-again', '/settings', '/order', '/cart']));
    this.showMobileFooter.set(shouldShowMobileFooter);
  }

  checkMarketingReferral(params: any) {

    let marketingReferral: MarketingReferral;

    const currentMarketingInfo: MarketingReferral | null = this.localStorageService.get(LOCALSTORAGE_KEYS.MARKETING_REFERRAL);

    if (params['utm_source'] && params['utm_medium'] && params['utm_campaign']) {
      marketingReferral = {
        utmCampaign: params['utm_campaign'],
        utmMedium: params['utm_medium'],
        utmSource: params['utm_source'],
      }
    } else if (currentMarketingInfo?.utmCampaign)
      marketingReferral = { ...currentMarketingInfo };
    else return this.signalStoreService.setIsLoadedMarketingValidation(true);

    checkSignupCouponCodes({
      marketingReferral,
      signalsStoreService: this.signalStoreService,
      localStorageService: this.localStorageService,
      promotionsService: this.#promotionsService,
      showDefaultMessage: false
    }).subscribe();

  }

  #identifyKlaviyoUser() {
    this.#hasSession$.pipe(
      tap((hasSession: boolean) => {
        if (hasSession)
          this.klaviyoService.identiyUser();
      })
    ).subscribe();
  }
}
